import CloseIcon from "@mui/icons-material/Close";
import {useLocale, useMessages} from "i18n";
import {formatMoney} from "pricing/PricingStorage";
import {ClaimButton, Copy, Detail, DismissButton, Message, Root} from "upsell/PostSignupOfferCallout.styles";
import type {ReferralOfferState} from "./useReferralOffer";

/**
 * The referred friend's gift in the create panel's notices slot (kitecorp/transferify-ui#1993,
 * api#1228).
 *
 * <b>The account page's card was not enough on its own.</b> A friend who follows a share link lands on
 * the home page and starts sending; nothing takes them to `/account`, and the 30-day window runs while
 * they never see the offer. This is the same gift on the surface they actually use.
 *
 * <b>Shares the post-signup callout's STYLES, not its component</b>, for the reason
 * {@link ../upsell/TrialExpiryOfferCallout} states: they are one gold offer strip to the reader, and a
 * second set of styles would be that surface drifting in two places. What differs is the eligibility
 * rule, which is not this file's — it arrives already decided as {@link ReferralOfferState.available}.
 *
 * <b>It shares the account card's WORDS too.</b> The two surfaces make the same offer to the same
 * reader, so a second wording would be a second promise to keep in step; `referralOffer.card` is
 * therefore the copy for both.
 *
 * <b>Dismissal hides it, it does not spend the claim.</b> The api's window is untouched and the picker
 * stays reachable from the account card, the pricing page and both limit surfaces.
 *
 * Mounted through {@link ../upsell/OfferCallouts} rather than directly, so the over-limit checkout
 * suppresses it with every other pitch (ui#1454: nothing competes with that purchase). The dialog it
 * opens is the panel's single {@link ./ReferralOfferDialog}, owned by `TransferPanel` — this file is
 * markup and two controls.
 */
export function ReferralGiftCallout({offer}: {offer: ReferralOfferState}) {
	const {referralOffer} = useMessages();
	const {locale} = useLocale();

	if (!offer.available || offer.model === null) {
		return null;
	}
	const model = offer.model;
	const friend = model.referrerName ?? referralOffer.friendFallback;
	// The cheapest option, which is the one the sentence quotes: the strip says what starting costs and
	// the picker is where the choice between the tiers is made. Same rule as the account card's.
	const cheapest = model.choices.reduce(
		(lowest, option) => option.intro < lowest.intro ? option : lowest, model.choices[0]);

	return (
		<Root data-testid="referral-gift-callout">
			<Copy>
				<Message variant="body2">{referralOffer.card.title(friend)}</Message>
				<Detail variant="caption">
					{referralOffer.card.body(formatMoney(model.currency, cheapest.intro, locale))}
				</Detail>
			</Copy>
			<ClaimButton
				size="small"
				disableElevation
				variant="contained"
				onClick={offer.show}
				data-testid="referral-gift-callout-open"
			>
				{referralOffer.card.cta}
			</ClaimButton>
			<DismissButton
				size="small"
				aria-label={referralOffer.card.dismissAria}
				onClick={offer.dismiss}
				data-testid="referral-gift-callout-dismiss"
			>
				<CloseIcon fontSize="small"/>
			</DismissButton>
		</Root>
	);
}
